// Phase 1.10 (hh-cn3) — DestructPreviewScreen.
//
// Full-screen preview shown before a destructive bulk / cascade delete is
// confirmed. The user sees the exact list of records that will disappear,
// the remaining lifetime of the confirmation token, and Cancel / Confirm
// buttons. Visual idiom is loosely ported from screens-feed.jsx
// (`DestructPreview` sheet) but rendered as a column screen (no overlay) so
// it can be pushed onto the router stack.
//
// Source design: designproject/files/screens-feed.jsx — DestructPreview.
// Backend (Phase 1.9 — hh-78y) returns the shape declared in
// `DestructPreviewResponse` below. Wire-up to the actual API lives in
// hh-46n; this component is pure and emits callbacks.
//
// Acceptance (case-30): the list is concrete — every affected entity is
// rendered explicitly, no "много записей" aggregation. Cancel emits
// `onCancel` and the parent decides not to call `onConfirm`, so the user's
// state stays untouched.

import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { T, Icon, Btn, SectionLabel, type IconName } from '../../tokens';
import { ScreenHeader } from '../../shell/ScreenHeader';

// ─────────────────────────────────────────────────────────
// API contract (forward declaration — duplicates the shape backend will
// return from POST /api/v1/destruct/preview; kept here while the typed API
// client at `api/endpoints/destruct.ts` is still a stub. hh-46n will reconcile.)
// ─────────────────────────────────────────────────────────

export interface DestructAffectedProperty {
  readonly id: string;
  readonly name: string;
}
export interface DestructAffectedRoom {
  readonly id: string;
  readonly name: string;
  readonly propertyName: string;
}
export interface DestructAffectedItem {
  readonly id: string;
  readonly name: string;
  readonly locationLabel: string;
}
export interface DestructAffectedEquipment {
  readonly id: string;
  readonly name: string;
  readonly locationLabel: string;
}

export interface DestructPreviewAffected {
  readonly properties: ReadonlyArray<DestructAffectedProperty>;
  readonly rooms: ReadonlyArray<DestructAffectedRoom>;
  readonly items: ReadonlyArray<DestructAffectedItem>;
  readonly equipment: ReadonlyArray<DestructAffectedEquipment>;
}

export interface DestructPreviewCounts {
  readonly properties: number;
  readonly rooms: number;
  readonly items: number;
  readonly equipment: number;
}

export interface DestructPreviewResponse {
  readonly confirmToken: string;
  readonly expiresAt: string;
  readonly affected: DestructPreviewAffected;
  readonly counts: DestructPreviewCounts;
}

// ─────────────────────────────────────────────────────────
// Safety pattern.
//
// Backend does not (yet) flag rows as safety-critical, so the screen does a
// conservative client-side regex check on the row's display name. The
// pattern matches Cyrillic stems for high-stakes equipment / utilities
// (gas, electrical, fire-extinguisher, boiler) plus a small English
// fallback ("water heater"). Anything matched is rendered with the
// `T.dangerSoft` tint as a defensive UX guard — the goal is to slow the
// user down on rows like "Газовая плита" / "Огнетушитель", not to be
// exhaustive. False negatives are acceptable; false positives are unlikely
// because the stems are domain-specific.
// ─────────────────────────────────────────────────────────

const SAFETY_PATTERN =
  /(газ|электр|огнетуш|котёл|котел|water\s*heater|бойлер)/i;

function isSafetySensitive(name: string): boolean {
  return SAFETY_PATTERN.test(name);
}

// ─────────────────────────────────────────────────────────
// Time helpers
// ─────────────────────────────────────────────────────────

function formatRemaining(msLeft: number): string {
  if (msLeft <= 0) return 'Время истекло';
  const totalSec = Math.floor(msLeft / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  if (mm <= 0) return `осталось ${ss} c`;
  return `осталось ${mm} мин ${ss.toString().padStart(2, '0')} c`;
}

function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

// ─────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────

export type DestructPreviewMode = 'bulk' | 'cascade';

export interface DestructPreviewScreenProps {
  readonly preview: DestructPreviewResponse;
  readonly mode?: DestructPreviewMode;
  readonly onCancel: () => void;
  readonly onConfirm: (confirmToken: string) => void;
}

interface Row {
  readonly key: string;
  readonly icon: IconName;
  readonly name: string;
  readonly sub?: string;
}

interface Group {
  readonly label: string;
  readonly rows: ReadonlyArray<Row>;
}

export function DestructPreviewScreen({
  preview,
  mode = 'bulk',
  onCancel,
  onConfirm,
}: DestructPreviewScreenProps): JSX.Element {
  const expiresMs = useMemo(() => Date.parse(preview.expiresAt), [preview.expiresAt]);
  const now = useNow();
  const msLeft = expiresMs - now;
  const expired = msLeft <= 0;

  const groups = useMemo<ReadonlyArray<Group>>(() => {
    const out: Group[] = [];
    const { affected } = preview;
    if (affected.properties.length > 0) {
      out.push({
        label: 'Объекты',
        rows: affected.properties.map((p) => ({
          key: `prop:${p.id}`,
          icon: 'home',
          name: p.name,
        })),
      });
    }
    if (affected.rooms.length > 0) {
      out.push({
        label: 'Помещения',
        rows: affected.rooms.map((r) => ({
          key: `room:${r.id}`,
          icon: 'folder',
          name: r.name,
          sub: r.propertyName,
        })),
      });
    }
    if (affected.items.length > 0) {
      out.push({
        label: 'Вещи',
        rows: affected.items.map((i) => ({
          key: `item:${i.id}`,
          icon: 'item',
          name: i.name,
          sub: i.locationLabel,
        })),
      });
    }
    if (affected.equipment.length > 0) {
      out.push({
        label: 'Оборудование',
        rows: affected.equipment.map((e) => ({
          key: `eq:${e.id}`,
          icon: 'boiler',
          name: e.name,
          sub: e.locationLabel,
        })),
      });
    }
    return out;
  }, [preview]);

  const totalAffected =
    preview.counts.properties +
    preview.counts.rooms +
    preview.counts.items +
    preview.counts.equipment;

  const title =
    mode === 'cascade'
      ? 'Удалить вместе с содержимым?'
      : 'Удалить выбранные записи?';

  const subtitle = `Затронет ${totalAffected} ${pluralRecords(totalAffected)}. После подтверждения ещё сутки можно откатить.`;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: T.bg,
      }}
    >
      <ScreenHeader title={title} subtitle={subtitle} onBack={onCancel} />

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 16px' }}>
        {groups.length === 0 ? (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              color: T.ink3,
              fontSize: 13,
            }}
          >
            Backend вернул пустой preview.
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.label}>
              <SectionLabel style={{ padding: '14px 4px 6px' }}>
                {group.label}
              </SectionLabel>
              {group.rows.map((row) => (
                <DestructRow key={row.key} row={row} />
              ))}
            </div>
          ))
        )}
      </div>

      <div
        style={{
          padding: '10px 16px 16px',
          borderTop: `1px solid ${T.border}`,
          background: T.bg,
        }}
      >
        <div
          style={{
            fontSize: 12,
            color: expired ? T.danger : T.ink3,
            fontWeight: 600,
            textAlign: 'center',
            marginBottom: 10,
          }}
        >
          {expired ? 'Время истекло — попроси заново' : formatRemaining(msLeft)}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn kind="ghost" onClick={onCancel} style={{ flex: 1 }}>
            Отказаться
          </Btn>
          <Btn
            kind="danger"
            icon="trash"
            onClick={() => onConfirm(preview.confirmToken)}
            disabled={expired}
            style={{ flex: 1.4 }}
          >
            Да, удалить
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Row
// ─────────────────────────────────────────────────────────

function DestructRow({ row }: { readonly row: Row }): JSX.Element {
  const safety = isSafetySensitive(row.name);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        background: safety ? T.dangerSoft : T.surface,
        borderRadius: 12,
        border: `1px solid ${safety ? '#E9C7BF' : T.border}`,
        marginBottom: 6,
      }}
    >
      <Icon
        name={row.icon}
        size={18}
        color={safety ? T.danger : T.ink2}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: safety ? T.danger : T.ink,
          }}
        >
          {row.name}
        </div>
        {row.sub !== undefined && (
          <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>
            {row.sub}
          </div>
        )}
      </div>
      {safety && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: T.danger,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          safety
        </div>
      )}
    </div>
  );
}

function pluralRecords(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'запись';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'записи';
  return 'записей';
}
